import { registerComponent, asyncRequest, debounce, loading } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "cart-items",
  class CartItems extends WYATTElement {
    constructor() {
      super();

      this.removeButtons = this.querySelectorAll("[data-remove-item-button]");
      this.form = this.querySelector("form");
      this.currentItemCount = Array.from(
        this.querySelectorAll('[name="updates[]"]'),
      ).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0,
      );

      this.removeButtons.forEach((button) => {
        button.addEventListener("click", (event) => {
          this.pendingUpdate = this.remove(event);
        });
      });

      const debouncedChangeHandler = debounce(() => this.flushChange(), 300);

      this.form?.addEventListener("change", (event) => {
        this.queuedChange = event;
        debouncedChangeHandler();
      });
      this.form?.addEventListener("submit", this.handleSubmit.bind(this));
    }

    /**
     * Send the debounced quantity change now, if one is waiting.
     * @returns {Promise<void>|undefined} The in-flight cart update, if any
     */
    flushChange() {
      if (this.queuedChange) {
        this.pendingUpdate = this.handleChange(this.queuedChange);
        this.queuedChange = null;
      }

      return this.pendingUpdate;
    }

    /**
     * Checkout trusts the `hasBackorder` attribute, so finish any cart change
     * and recalculate it before letting the checkout submit through.
     * @param {SubmitEvent} event
     */
    async handleSubmit(event) {
      const { submitter } = event;
      if (submitter?.name !== "checkout") return;

      event.preventDefault();
      if (this.checkingOut) return;

      this.checkingOut = true;
      loading.add(this);

      try {
        await this.flushChange();
      } catch (error) {
        window.console.error(
          "Cart: pending change failed before checkout",
          error,
        );
      }
      await WYATT.Cart.syncBackorder();

      // A cart change may have re-rendered the form, so submit the current one
      const form = document.getElementById(this.form.id);
      if (form) {
        const checkoutInput = document.createElement("input");
        checkoutInput.type = "hidden";
        checkoutInput.name = submitter.name;
        checkoutInput.value = submitter.value;
        form.append(checkoutInput);
        form.submit();
        checkoutInput.remove();
      }

      loading.remove(this);
      this.checkingOut = false;
    }

    async removeOrphanedPlans() {
      const cartResponse = await fetch("/cart?view=protection-plan");
      const { items } = await cartResponse.json();
      if (!items.length) return null;

      const planItems = items.filter(({ is_plan: isPlan }) => isPlan);
      if (!planItems.length) return null;

      const regularItems = items.filter((item) => !item.is_plan);
      const itemsToRemove = planItems.filter(
        (planItem) =>
          !regularItems.some((item) =>
            item.plans.some((plan) => plan.variant_id === planItem.variant_id),
          ),
      );
      if (!itemsToRemove.length) return null;

      const updates = itemsToRemove.reduce((acc, { variant_id: varId }) => {
        acc[varId] = 0;
        return acc;
      }, {});

      return WYATT.Cart.update({
        updates,
        sections: WYATT.Cart.getSectionNames(),
      });
    }

    async remove(event) {
      const line = event.currentTarget.dataset.removeItemButton;

      event.preventDefault();

      const response = await this.updateQuantity(line, 0);
      if (response) {
        await this.handleSuccessfulUpdate(response);
      }
      loading.remove(this);
    }

    async handleChange(event) {
      const { lineItemIndex } = event.target.dataset;
      if (!lineItemIndex) {
        loading.remove(this);
        return;
      }
      const response = await this.updateQuantity(
        lineItemIndex,
        event.target.value,
      );
      if (response) {
        await this.handleSuccessfulUpdate(response);
      }
      loading.remove(this);
    }

    async syncRecyclingFees() {
      const {
        mattress: mattressFeeVariantId,
        foundation: foundationFeeVariantId,
      } = this.config.recyclingFeeVariantIdsByType ?? {};
      if (!mattressFeeVariantId && !foundationFeeVariantId) return null;

      const cartResponse = await fetch("/cart?view=recycling-fee");
      const { items } = await cartResponse.json();
      if (!items.length) return null;

      const isFeeVariant = (varId) =>
        varId === mattressFeeVariantId || varId === foundationFeeVariantId;

      const parentQuantities = new Map();
      const mattressVariantIds = new Set();
      const foundationVariantIds = new Set();
      items.forEach(
        ({
          variant_id: varId,
          quantity,
          is_mattress: isMattress,
          is_foundation: isFoundation,
        }) => {
          if (isFeeVariant(varId)) return;
          const id = String(varId);
          parentQuantities.set(id, (parentQuantities.get(id) ?? 0) + quantity);
          if (isMattress) mattressVariantIds.add(id);
          if (isFoundation || isMattress) foundationVariantIds.add(id);
        },
      );

      const updates = items.reduce(
        (acc, { key, variant_id: varId, parent_key: parentKey, quantity }) => {
          if (!isFeeVariant(varId)) return acc;

          const eligibleParents =
            varId === mattressFeeVariantId
              ? mattressVariantIds
              : foundationVariantIds;
          // Customers choose how many items to recycle, so only cap fees at
          // the covered item's quantity rather than matching it.
          const target = eligibleParents.has(parentKey)
            ? Math.min(quantity, parentQuantities.get(parentKey))
            : 0;

          if (target !== quantity) acc[key] = target;
          return acc;
        },
        {},
      );
      if (!Object.keys(updates).length) return null;

      return WYATT.Cart.update({
        updates,
        sections: WYATT.Cart.getSectionNames(),
      });
    }

    async updateQuantity(line, quantity) {
      const body = {
        line,
        quantity,
        sections: WYATT.Cart.getSectionNames(),
        sections_url: window.location.pathname,
      };
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      await WYATT.Cart.ready;

      return asyncRequest(WYATT.routes.cart_change_url, options, {
        messageOptions: {
          minDelay: 6000,
          appendPoint:
            '[data-mini-cart][active="true"] [data-dialog-messages], #page-messages',
        },
      });
    }

    async handleSuccessfulUpdate(state) {
      const parsedState = JSON.parse(state);

      let latestResult = await WYATT.Cart.applyChangeResponse(parsedState);

      const planRemoval = await this.removeOrphanedPlans();
      if (planRemoval) latestResult = planRemoval;

      const feeSync = await this.syncRecyclingFees();
      if (feeSync) latestResult = feeSync;

      if (latestResult?.sections) {
        this.publish(
          WYATT.PubSub.events.updateDOM,
          WYATT.Cart.sectionUpdates(latestResult.sections),
        );
      }

      WYATT.Cart.syncBackorder();
    }
  },
);
